import { useState, useEffect, useCallback, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
  Popup,
} from "react-leaflet";
import L from "leaflet";
import { Search, MapPin, Crosshair, X, Check } from "lucide-react";
import "leaflet/dist/leaflet.css";

// Fix for default marker icons in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom marker icon with color support
const createCustomIcon = (color: string = "#3B82F6") => {
  return new L.Icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color.replace('#', '')}.png`,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });
};

const defaultIcon = createCustomIcon("#3B82F6");
const defaultPosition: [number, number] = [4.175278, 73.508889]; // Malé

interface MapLocationPickInputProps {
  value?: [number, number] | null;
  onChange?: (lat: number, lng: number) => void;
  height?: number;
  placeholder?: string;
  showSearch?: boolean;
  showCurrentLocation?: boolean;
  draggableMarker?: boolean;
  zoom?: number;
  disabled?: boolean;
}

/* ---------------- MAP CLICK HANDLER ---------------- */
function LocationPicker({ 
  onSelect, 
  disabled 
}: { 
  onSelect: (pos: [number, number]) => void;
  disabled?: boolean;
}) {
  useMapEvents({
    click(e) {
      if (!disabled) {
        onSelect([e.latlng.lat, e.latlng.lng]);
      }
    },
  });
  return null;
}

/* ---------------- MAP CENTER SYNC ---------------- */
function MapCenterUpdater({ position }: { position: [number, number] }) {
  const map = useMap();

  useEffect(() => {
    map.setView(position, map.getZoom(), { animate: true });
  }, [position, map]);

  return null;
}

/* ---------------- DRAGGABLE MARKER ---------------- */
function DraggableMarker({ 
  position, 
  onPositionChange,
  disabled 
}: { 
  position: [number, number];
  onPositionChange: (pos: [number, number]) => void;
  disabled?: boolean;
}) {
  const markerRef = useRef<L.Marker>(null);

  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker && !disabled) {
        const pos = marker.getLatLng();
        onPositionChange([pos.lat, pos.lng]);
      }
    },
  };

  return (
    <Marker
      draggable={!disabled}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
      icon={defaultIcon}
    >
      <Popup>
        <div className="text-sm">
          <strong>Selected Location</strong>
          <br />
          Lat: {position[0].toFixed(5)}
          <br />
          Lng: {position[1].toFixed(5)}
        </div>
      </Popup>
    </Marker>
  );
}

/* ---------------- SEARCH CONTROL ---------------- */
function SearchControl({ onSearchResult }: { onSearchResult: (pos: [number, number]) => void }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    
    setIsSearching(true);
    try {
      // Using Nominatim OpenStreetMap API
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchTerm)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        onSearchResult([parseFloat(lat), parseFloat(lon)]);
      } else {
        alert("Location not found");
      }
    } catch (error) {
      console.error("Search error:", error);
      alert("Error searching location");
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="absolute top-2 left-2 z-[1000] bg-white dark:bg-gray-800 rounded-lg shadow-lg">
      <div className="flex items-center gap-1 p-1">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          placeholder="Search location..."
          className="px-3 py-1.5 text-sm border-0 focus:outline-none rounded-l-lg w-48 bg-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
        />
        <button
          onClick={handleSearch}
          disabled={isSearching}
          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-r-lg transition-colors"
        >
          {isSearching ? (
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <Search className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          )}
        </button>
      </div>
    </div>
  );
}

/* ---------------- MAIN COMPONENT ---------------- */
export default function MapLocationPickInput({
  value = null,
  onChange,
  height = 360,
  placeholder = "Click on map to select location",
  showSearch = true,
  showCurrentLocation = true,
  draggableMarker = true,
  zoom = 13,
  disabled = false,
}: MapLocationPickInputProps) {
  const [userLocation, setUserLocation] = useState<[number, number]>(defaultPosition);
  const [selectedPos, setSelectedPos] = useState<[number, number] | null>(value);
  const [isLocating, setIsLocating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ---- Sync external value ---- */
  useEffect(() => {
    if (value) {
      setSelectedPos(value);
      setError(null);
    }
  }, [value]);

  /* ---- Get user GPS ---- */
  const getUserLocation = useCallback(() => {
    setIsLocating(true);
    setError(null);
    
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      setIsLocating(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const location: [number, number] = [pos.coords.latitude, pos.coords.longitude];
        setUserLocation(location);
        if (!selectedPos && !value) {
          setSelectedPos(location);
          onChange?.(location[0], location[1]);
        }
        setIsLocating(false);
      },
      (err) => {
        console.error("Geolocation error:", err);
        setError("Unable to get your location. Using default location.");
        setUserLocation(defaultPosition);
        setIsLocating(false);
      }
    );
  }, [selectedPos, value, onChange]);

  useEffect(() => {
    getUserLocation();
  }, [getUserLocation]);

  const handleSelect = useCallback((pos: [number, number]) => {
    if (disabled) return;
    setSelectedPos(pos);
    setError(null);
    onChange?.(pos[0], pos[1]);
  }, [onChange, disabled]);

  const handleUseCurrentLocation = () => {
    if (disabled) return;
    getUserLocation();
    if (userLocation) {
      handleSelect(userLocation);
    }
  };

  const handleClear = () => {
    if (disabled) return;
    setSelectedPos(null);
    onChange?.(0, 0);
  };

  const center = selectedPos ?? userLocation;

  return (
    <div className="flex flex-col gap-3 w-full">
      {/* Map Container */}
      <div
        className={`w-full rounded-xl overflow-hidden border shadow-sm transition-all ${
          disabled ? "opacity-60 cursor-not-allowed" : ""
        } ${error ? "border-red-300 dark:border-red-700" : "border-gray-200 dark:border-gray-700"}`}
        style={{ height }}
      >
        <MapContainer 
          center={center} 
          zoom={zoom} 
          className="h-full w-full"
          style={{ background: "#f0f0f0 dark:bg-gray-800" }}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            className="dark:brightness-75"
          />

          <LocationPicker onSelect={handleSelect} disabled={disabled} />
          <MapCenterUpdater position={center} />

          {selectedPos && (
            draggableMarker ? (
              <DraggableMarker 
                position={selectedPos} 
                onPositionChange={handleSelect}
                disabled={disabled}
              />
            ) : (
              <Marker position={selectedPos} icon={defaultIcon}>
                <Popup>
                  <div className="text-sm dark:text-gray-200">
                    <strong>Selected Location</strong>
                    <br />
                    Lat: {selectedPos[0].toFixed(5)}
                    <br />
                    Lng: {selectedPos[1].toFixed(5)}
                  </div>
                </Popup>
              </Marker>
            )
          )}

          {/* Search Control */}
          {showSearch && !disabled && (
            <SearchControl onSearchResult={handleSelect} />
          )}

          {/* Current Location Button */}
          {showCurrentLocation && !disabled && (
            <div className="absolute bottom-2 right-2 z-[1000]">
              <button
                onClick={handleUseCurrentLocation}
                disabled={isLocating}
                className="bg-white dark:bg-gray-800 p-2 rounded-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                title="Use my current location"
              >
                {isLocating ? (
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Crosshair className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                )}
              </button>
            </div>
          )}
        </MapContainer>
      </div>

      {/* Selected Location Info */}
      {selectedPos && (
        <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Selected Location</p>
                <p className="text-sm font-mono text-gray-900 dark:text-gray-100">
                  {selectedPos[0].toFixed(6)}, {selectedPos[1].toFixed(6)}
                </p>
                <a
                  href={`https://www.openstreetmap.org/?mlat=${selectedPos[0]}&mlon=${selectedPos[1]}#map=15/${selectedPos[0]}/${selectedPos[1]}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 mt-1 inline-block"
                >
                  Open in OpenStreetMap →
                </a>
              </div>
            </div>
            {!disabled && (
              <button
                onClick={handleClear}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                title="Clear selection"
              >
                <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded-md border border-red-200 dark:border-red-800">
          ⚠️ {error}
        </div>
      )}

      {/* Placeholder when no location selected */}
      {!selectedPos && !error && (
        <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/30 p-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-center">
          {placeholder}
        </div>
      )}
    </div>
  );
}